import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { KeywordForSaveResponse, getKeywordForSave, saveKeyword } from 'core'

import { Button, Col, DefaultInputV2, DefaultRadioGroupV2, DefaultSelectV2, Modal, Row } from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'

export type SaveKeywordModalProps = {
  onCancel: () => void
  onOk: () => void
  keywordId: string | null
  visible: boolean
}

const SaveKeywordModal = (props: SaveKeywordModalProps) => {
  const { t: translate } = useTranslation('translation')

  const [initialValues, setInitialValues] = useState<KeywordForSaveResponse>(null)
  const { data: keyword, reload } = useQueryRequestV2<KeywordForSaveResponse>({
    initialState: null,
    queryFn: async () => await getKeywordForSave(props.keywordId),
  })

  useEffect(() => {
    ;(async () => {
      await reload()
    })()
  }, [props.keywordId])

  useEffect(() => {
    if (keyword) {
      setInitialValues(keyword)
    }
  }, [keyword])

  const onSubmit = async (values: KeywordForSaveResponse) => {
    await saveKeyword({
      keywordId: keyword.keywordId,
      keyword: values.keyword,
      expenseTypes: values.expenseTypes,
      exceptions: values.exceptions,
      Type: values.type,
      status: values.status,
    })
    props.onOk()
  }

  return (
    <>
      {initialValues && (
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          validateOnMount={true}
          validateOnChange={true}
          initialValues={initialValues}
          onSubmit={(values: KeywordForSaveResponse) => onSubmit(values)}
        >
          {(formik) => {
            return (
              <Modal
                title={'Save Keyword'}
                visible={props.visible}
                destroyOnClose
                closable
                onCancel={props.onCancel}
                onOk={() => onSubmit(formik.values)}
                okButtonProps={{ disabled: !formik.isValid }}
              >
                <form onSubmit={formik.handleSubmit} autoComplete="off">
                  <Row className={'mb-24'}>
                    <Col span={24}>
                      <DefaultInputV2 label={'Keyword'} name="keyword" id="keyword" />
                    </Col>
                  </Row>
                  <Row className={'mb-24'}>
                    <Col span={24}>
                      <DefaultSelectV2
                        name="expenseTypes"
                        mode="multiple"
                        allowClear
                        placeholder="Please select"
                        showSearch={true}
                        items={keyword.availableExpenseTypes}
                      />
                    </Col>
                  </Row>
                  <Row className={'mb-48'}>
                    {formik.values.exceptions.map((x, index) => (
                      <Row className={'mb-24'}>
                        <Col>
                          <DefaultInputV2
                            label={'Exception'}
                            name={`exceptions[${index}]`}
                            placeholder={'add exception'}
                          />
                        </Col>
                        <Col>
                          <Button
                            type="default"
                            className={'mt-24 ml-32'}
                            size={'medium'}
                            htmlType="button"
                            onClick={() => {
                              const actual = {
                                ...formik.values,
                              }
                              const newExceptions = actual.exceptions.filter((_, i) => i !== index)
                              console.log(newExceptions)
                              formik.setFieldValue('exceptions', newExceptions)
                            }}
                          >
                            Remove Exception
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={() => {
                        const actual = {
                          ...formik.values,
                        }
                        const newExceptions = [...actual.exceptions, '']
                        formik.setFieldValue('exceptions', newExceptions)
                      }}
                    >
                      {formik.values.exceptions.length === 0 ? 'Add Exception' : 'Add another Exception'}
                    </Button>
                  </Row>
                  <Row className={'mb-24'}>
                    <DefaultRadioGroupV2
                      items={[
                        { name: 'Deny', value: 0 },
                        { name: 'NEI', value: 1 },
                      ]}
                      name="type"
                    />
                  </Row>
                  <Row>
                    <DefaultRadioGroupV2
                      items={[
                        { name: 'Active', value: 1 },
                        { name: 'Inactive', value: 0 },
                      ]}
                      name="status"
                    />
                  </Row>
                </form>
              </Modal>
            )
          }}
        </Formik>
      )}
    </>
  )
}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    keyword: Yup.string().nullable().required(translate('general.required')),
    exceptions: Yup.array().of(Yup.string().required(translate('general.required'))),
  })

export default SaveKeywordModal
